import { queryOptions, mutationOptions } from '@tanstack/react-query';
import { portalLocaleExtendedToPortalLocale, type PortalLocale } from '@worldcoo/monolith/portals';
import type { ContactForm } from '@worldcoo/monolith/portals/contact-form-schema';
import {
	getPortalAuthorFn,
	getPortalMetadataFn,
	getPortalCampaignMetadataFn,
	getPortalCampaignsFn,
	getPortalCampaignFn,
	getPortalCampaignsAggregatesFn,
	refreshPortalAggregatesCacheFn,
} from './server-functions-portal';
import {
	preregisterDonationFn,
	getPaymentDetailsFn,
	processPaymentFn,
	postContactFormFn,
} from './server-functions-payments';
import type { SketchDonationInput, PaymentDetailsInput, ProcessPaymentInput } from '@worldcoo/monolith/payments/shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { notFound, useSearch, useRouteContext } from '@tanstack/react-router';
import { useEphemeralStore } from './app-store';
import type { CategoryKey } from '@worldcoo/monolith/core';
import { useDeferredValue } from 'react';

/**
 * MARK: getPortalCampaignsAggregates
 */
export const portalCampaignsAggregatesOptions = ({ portalId }: { portalId: string }) =>
	queryOptions({
		queryKey: [portalId, 'campaigns-aggregates'],
		queryFn: () => getPortalCampaignsAggregatesFn({ data: { portalId } }),
	});

export function usePortalCampaignsAggregatesQuery() {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(portalCampaignsAggregatesOptions({ portalId }));
	return data;
}

/**
 * MARK: getPortalAuthor
 */
export const portalAuthorOptions = ({ portalId, locale }: { portalId: string; locale: PortalLocale }) =>
	queryOptions({
		queryKey: [portalId, locale, 'author'],
		queryFn: () => getPortalAuthorFn({ data: { portalId, locale } }),
	});
export function usePortalAuthorQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalAuthorOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalMetadata
 */
export const portalMetadataOptions = ({ portalId, locale }: { portalId: string; locale: PortalLocale }) =>
	queryOptions({
		queryKey: [portalId, locale, 'metadata'],
		queryFn: () => getPortalMetadataFn({ data: { portalId, locale } }),
	});
export function usePortalMetadataQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalMetadataOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaignMetadata
 */
export const portalCampaignMetadataOptions = ({
	portalId,
	locale,
	campaignId,
}: {
	portalId: string;
	locale: PortalLocale;
	campaignId: string;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', campaignId, 'metadata'],
		queryFn: () => getPortalCampaignMetadataFn({ data: { portalId, campaignId, locale } }),
	});
export function usePortalCampaignMetadataQuery(campaignId: string) {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalCampaignMetadataOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaigns
 */
export const portalCampaignsOptions = ({
	portalId,
	locale,
	query,
	year,
	state,
	causes,
	city,
}: {
	portalId: string;
	locale: PortalLocale;
	query: string | undefined;
	year: number | undefined;
	state: 'finished' | undefined;
	causes: CategoryKey[] | undefined;
	city: string | undefined;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', query, year, state, causes, city],
		queryFn: () => getPortalCampaignsFn({ data: { portalId, locale, query, year, state, causes, city } }),
	});
export function usePortalCampaignsQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang, city } = useSearch({ from: '__root__' });
	const { q, year, state, causes } = useSearch({ from: '/$portalId/' });
	const deferredFilters = useDeferredValue({
		portalId,
		locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		query: q,
		year,
		state,
		causes,
		city,
	});
	const { data } = useSuspenseQuery(portalCampaignsOptions(deferredFilters));
	if ('notFound' in data) return notFound();
	return data;
}

/**
 * MARK: getPortalCampaign
 */
export const portalCampaignOptions = ({
	portalId,
	locale,
	campaignId,
}: {
	portalId: string;
	locale: PortalLocale;
	campaignId: string;
}) =>
	queryOptions({
		queryKey: [portalId, locale, 'campaigns', campaignId],
		queryFn: () => getPortalCampaignFn({ data: { portalId, locale, campaignId } }),
	});
export function usePortalCampaignQuery(campaignId: string) {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		portalCampaignOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	if ('notFound' in data) return notFound();
	return { data, isPageLoaded: pagesLoaded.includes(`campaign-${campaignId}`) };
}

/**
 * MARK: preregisterDonation
 */
export const preregisterDonationOptions = () =>
	mutationOptions({
		mutationKey: ['preregisterDonation'],
		mutationFn: (input: SketchDonationInput) => preregisterDonationFn({ data: input }),
	});

/**
 * MARK: getPaymentDetails
 */
export const getPaymentDetailsOptions = () =>
	mutationOptions({
		mutationKey: ['getPaymentDetails'],
		mutationFn: (input: PaymentDetailsInput) => getPaymentDetailsFn({ data: input }),
	});
/**
 * MARK: processPayment
 */
export const processPaymentOptions = () =>
	mutationOptions({
		mutationKey: ['processPayment'],
		mutationFn: (input: ProcessPaymentInput) => processPaymentFn({ data: input }),
	});

/**
 * MARK: refreshPortalAggregatesCache
 */
export const refreshPortalAggregatesCacheOptions = () =>
	mutationOptions({
		mutationKey: ['refreshPortalAggregatesCache'],
		mutationFn: (input: { portalId: string }) => refreshPortalAggregatesCacheFn({ data: input }),
	});

/**
 * MARK: postContactForm
 */
export const postContactFormOptions = () =>
	mutationOptions({
		mutationKey: ['postContactForm'],
		mutationFn: (input: ContactForm) => postContactFormFn({ data: input }),
	});
